import { UnstyledButton } from '@mantine/core';
import { ReactNode } from 'react';
import { FONT_WEIGHT } from 'src/config/theme';
import { SurveyModel } from 'survey-core';
import { MappedRubricRow } from './Rubric';

const isFieldValueSelected = (value: unknown, fieldValue: string | number) => {
  return Array.isArray(value) && (value as (string | number)[])?.includes(fieldValue);
};

const CustomToggleButton = (props: {
  id: string | number | undefined;
  disabled: boolean;
  displayValue: ReactNode;
  cellAlign?: 'center' | 'left' | 'right' | 'inherit' | 'justify';
  cellVerticalAlign?: 'top' | 'middle' | 'bottom';
  fieldValue: string | number;
  field: unknown;
  questionName: string;
  value: unknown;
  row: MappedRubricRow;
  setValue: (value: unknown) => void;
  survey: SurveyModel;
}) => {
  const { disabled, displayValue, cellAlign, cellVerticalAlign, fieldValue, value, setValue } =
    props;

  const selected = isFieldValueSelected(value, fieldValue);

  const handleToggleClick = (responseValue: unknown) => {
    let updatedValue = value ? JSON.parse(JSON.stringify(value)) : [];

    if (!Array.isArray(updatedValue)) {
      updatedValue = [];
    }

    if (!selected) {
      const rowCellValues = props.row.cells.map((cell) => cell.value);
      if (
        updatedValue.length > 0 &&
        updatedValue.some((val: unknown) => rowCellValues.includes(val as string))
      ) {
        // remove any other selected values from the same row
        updatedValue = updatedValue.filter(
          (val: unknown) => !rowCellValues.includes(val as string)
        );
      }
      updatedValue.push(responseValue);
    } else {
      updatedValue = updatedValue?.length
        ? updatedValue.filter((v: unknown) => v !== responseValue)
        : [];
    }

    setValue(updatedValue); // Notify parent if callback provided
  };

  const cssVariables = props.survey.themeVariables || {};
  const primaryBackcolor = cssVariables['--sjs2-primary-backcolor'] || 'rgba(25, 179, 148, 1)';
  const primaryBackcolorLight =
    cssVariables['--sjs2-primary-backcolor-light'] || 'rgba(25, 179, 148, 0.1)';
  const primaryBackcolorDark = cssVariables['--sjs2-primary-backcolor-dark'] || primaryBackcolor;
  const generalForecolor = cssVariables['--sjs2-general-forecolor'] || 'rgba(0, 0, 0, 0.91)';
  const cornerRadius = cssVariables['--sjs2-corner-radius'] || '4px';

  return (
    <UnstyledButton
      id={fieldValue?.toString()}
      style={{
        textTransform: 'none',
        height: '100%',
        width: '100%',
        fontSize: '1em',
        textAlign: cellAlign,
        alignItems:
          cellVerticalAlign === 'top' ? 'start' : cellVerticalAlign === 'bottom' ? 'end' : 'center',
        display: 'flex',
        justifyContent: 'center',
        color: generalForecolor,
        fontWeight: FONT_WEIGHT.MEDIUM,
        borderRadius: cornerRadius,
        backgroundColor: selected ? primaryBackcolorLight : 'transparent',
        border: selected ? `2px solid ${primaryBackcolor}` : '2px solid transparent',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
      onMouseEnter={(event) => {
        if (!disabled) {
          event.currentTarget.style.backgroundColor = primaryBackcolorLight;
          event.currentTarget.style.borderColor = primaryBackcolorDark;
        }
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.backgroundColor = selected
          ? primaryBackcolorLight
          : 'transparent';
        event.currentTarget.style.borderColor = selected ? primaryBackcolor : 'transparent';
      }}
      disabled={disabled}
      value={fieldValue || ''}
      onClick={() => handleToggleClick(fieldValue || '')}
    >
      {displayValue}
    </UnstyledButton>
  );
};

export default CustomToggleButton;
